/**
 * 현재 날짜를 원하는 형식으로 포맷팅하는 함수
 * @param format 날짜 형식 (예: 'YYYY-MM-DD', 'YYYY.MM', 'MM/DD/YYYY')
 * @param date 포맷팅할 날짜 (기본값: 현재 날짜)
 * @returns 포맷팅된 날짜 문자열
 */
export const formatDate = (format: string, date: Date = new Date()): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  const dayNames = ['일', '월', '화', '수', '목', '금', '토']
  const dayOfWeek = dayNames[date.getDay()]

  // 형식 토큰을 실제 값으로 치환
  const replacements: Record<string, string> = {
    YYYY: String(year),
    YY: String(year).slice(-2),
    MM: month,
    M: String(date.getMonth() + 1),
    DD: day,
    D: String(date.getDate()),
    HH: hours,
    H: String(date.getHours()),
    mm: minutes,
    m: String(date.getMinutes()),
    ss: seconds,
    s: String(date.getSeconds()),
    EEE: dayOfWeek,
  }

  // 긴 토큰부터 치환 (YYYY가 YY보다 먼저 처리되도록)
  const sortedTokens = Object.keys(replacements).sort(
    (a, b) => b.length - a.length
  )

  let result = format
  result = result.replace(' mm분', date.getMinutes() === 0 ? '' : ' mm분')

  sortedTokens.forEach(token => {
    result = result.replace(new RegExp(token, 'g'), replacements[token])
  })

  return result
}

/**
 * begin 날짜와 serviceTime을 받아서 YYYY-MM-DD HH:mm:ss 형식의 begin, end 반환
 */
export const calculateDateRange = ({
  beginDateTime,
  serviceTime,
}: {
  beginDateTime: Date
  serviceTime: number
}): {
  begin: string
  end: string
} => {
  const beginDate = new Date(beginDateTime)
  const endDate = new Date(beginDate.getTime() + serviceTime * 60 * 1000)

  const formatDateTime = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
  }

  return {
    begin: formatDateTime(beginDate),
    end: formatDateTime(endDate),
  }
}

/**
 * 특정 날짜로부터 N일 후의 날짜를 반환
 * @param baseDate - 기준 날짜 (YYYY-MM-DD 형식)
 * @param days - 추가할 일수
 * @returns YYYY-MM-DD 형식의 날짜 문자열
 */
export const getDateAfterDays = (baseDate: string, days: number): string => {
  const date = new Date(baseDate)
  date.setDate(date.getDate() + days)

  return date.toISOString().split('T')[0]
}

/**
 * 초를 MM:SS 형식으로 변환
 * @param seconds - 초 단위 시간
 * @returns MM:SS 형식의 시간 문자열
 */
export const formatTimeFromSeconds = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

/**
 * 만료일을 YYYY.M.D까지 형식으로 변환
 * @param expireDate - ISO 형식의 만료일 문자열
 * @returns YYYY.M.D까지 형식의 문자열
 *
 * @example
 * ```typescript
 * formatExpireDate('2024-12-31T23:59:59')
 * // '2024.12.31까지'
 * ```
 */
export const formatExpireDate = (expireDate: string): string => {
  const date = new Date(expireDate)
  return `${date.getFullYear()}.${date.getMonth() + 1}.${date.getDate()}까지`
}

const NOON = 12

/**
 * '오후 1:00' — 12시간제 시각
 *
 * @description
 * 예약 시간은 화면마다 이 모양으로 보여 준다(웹예약·타이어). 자정은 '오전 12:00' 이다.
 */
export const formatMeridiemTime = ({
  hour,
  minute,
}: {
  hour: number
  minute: number
}): string => {
  const meridiem = hour < NOON ? '오전' : '오후'
  const displayHour = hour % NOON || NOON

  return `${meridiem} ${displayHour}:${String(minute).padStart(2, '0')}`
}
